// src/components/layout/Sidebar.jsx
import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  LayoutDashboard,
  Home,
  Building2,
  Briefcase,
  LogOut,
  AlertTriangle,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

// Logout Confirmation Modal
const LogoutConfirmationModal = ({ isOpen, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all duration-300">
        <div className="p-6">
          {/* Icon */}
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-amber-600" />
            </div>
          </div>
          
          {/* Content */}
          <div className="text-center mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Logout Confirmation
            </h3>
            <p className="text-gray-600">
              Are you sure you want to logout? You will need to login again to access the admin dashboard.
            </p>
          </div>
          
          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-xl font-medium hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg hover:shadow-xl"
            >
              Yes, Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Sidebar = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await logout();
      setShowLogoutModal(false);
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
      setShowLogoutModal(false);
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  const handleNavClick = () => {
    // Close sidebar on mobile when navigation item is clicked
    if (window.innerWidth < 1024) {
      onClose?.();
    }
  };

  const menuItems = [
    {
      name: 'Dashboard',
      path: '/admin/dashboard',
      icon: LayoutDashboard
    },
    {
      name: 'Villa Management',
      path: '/admin/villas',
      icon: Home
    },
    {
      name: 'Business Packages',
      path: '/admin/business',
      icon: Building2
    },
    {
      name: 'Services',
      path: '/admin/services',
      icon: Briefcase
    }
  ];

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50 bg-white shadow-xl
          transform transition-all duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isCollapsed ? 'lg:w-20' : 'lg:w-64'}
          w-64
        `}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-center px-6 py-3 border-b border-gray-200 bg-white relative">
            {/* Logo Full - When Expanded */}
            <div className={`transition-all duration-300 ${isCollapsed ? 'lg:opacity-0 lg:w-0 lg:hidden' : 'opacity-100'}`}>
              <img 
                src="/images/logo.png" 
                alt="Dewanta Villas" 
                className="h-10 w-auto object-contain"
              />
            </div>
            
            {/* Logo Small - When Collapsed (Desktop) - REPLACE THIS PATH WITH YOUR SMALL LOGO */}
            <div className={`hidden lg:flex items-center justify-center transition-all duration-300 ${isCollapsed ? 'opacity-100' : 'opacity-0 absolute'}`}>
              <img 
                src="/images/logo192.png" 
                alt="DV" 
                className="h-10 w-10 object-contain"
              />
              {/* If you don't have a small logo yet, you can use this placeholder */}
              {/* <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center shadow-md">
                <span className="text-white font-bold text-lg">DV</span>
              </div> */}
            </div>

            {/* Close Button (Mobile) */}
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors absolute right-4"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            {/* Collapse Toggle Button (Desktop) */}
            <button
              onClick={onToggleCollapse}
              className="hidden lg:block p-2 rounded-lg hover:bg-amber-50 transition-colors absolute -right-3 top-1/2 -translate-y-1/2 bg-white border-2 border-gray-200 shadow-md hover:border-amber-400"
            >
              {isCollapsed ? (
                <ChevronRight className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              )}
            </button>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 px-4 py-6 overflow-y-auto">
            <div className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={handleNavClick}
                    className={({ isActive }) =>
                      `group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                        isActive
                          ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg'
                          : 'text-gray-600 hover:bg-amber-50 hover:text-amber-700'
                      } ${isCollapsed ? 'lg:justify-center' : ''}`
                    }
                    title={isCollapsed ? item.name : ''}
                  >
                    {({ isActive }) => (
                      <>
                        <Icon 
                          className={`transition-colors duration-200 ${
                            isActive ? 'text-white' : 'text-gray-400 group-hover:text-amber-600'
                          } ${isCollapsed ? 'lg:mr-0 w-5 h-5' : 'w-5 h-5 mr-3'}`}
                        />
                        <span className={`transition-all duration-300 ${isCollapsed ? 'lg:hidden' : ''}`}>
                          {item.name}
                        </span>
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </nav>

          {/* User Section */}
          <div className="flex-shrink-0 border-t border-gray-200 p-4 bg-gray-50">
            {/* User Info */}
            {!isCollapsed && (
              <div className="flex items-center mb-3 px-4 py-3 bg-white rounded-xl shadow-sm border border-gray-200 lg:block">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center shadow-md mx-auto">
                    <span className="text-sm font-semibold text-white">
                      {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                    </span>
                  </div>
                </div>
                <div className="ml-3 flex-1 min-w-0 lg:ml-0 lg:mt-2 lg:text-center">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {user?.name || 'Admin User'}
                  </p>
                  <p className="text-xs text-amber-600 capitalize font-medium">
                    {user?.role || 'Administrator'}
                  </p>
                </div>
              </div>
            )}

            {/* User Avatar Only (when collapsed) */}
            {isCollapsed && (
              <div className="hidden lg:flex items-center justify-center mb-3">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center shadow-md">
                  <span className="text-sm font-semibold text-white">
                    {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                  </span>
                </div>
              </div>
            )}

            {/* Logout Button */}
            <button
              onClick={handleLogoutClick}
              className={`flex items-center w-full px-4 py-3 text-sm font-medium text-gray-600 rounded-xl hover:bg-red-50 hover:text-red-600 transition-all duration-200 group border border-transparent hover:border-red-200 ${
                isCollapsed ? 'lg:justify-center' : ''
              }`}
              title={isCollapsed ? 'Logout' : ''}
            >
              <LogOut className={`group-hover:text-red-600 transition-colors ${isCollapsed ? 'lg:mr-0 w-5 h-5' : 'w-5 h-5 mr-3'}`} />
              <span className={`transition-all duration-300 ${isCollapsed ? 'lg:hidden' : ''}`}>
                Logout
              </span>
            </button>
          </div>
        </div>
      </aside>

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        isOpen={showLogoutModal}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
      />
    </>
  );
};

export default Sidebar;